// Phase 2 — Feature flag for the new video draft preview-then-commit flow.
//
// During the beta we only enable it for `animated-problem` style, regardless
// of who the user is. The decision is intentionally made client-side because:
//   - The new flow is purely additive on the backend (legacy endpoints stay
//     untouched), so a single misbehaving user can't damage shared state.
//   - We want to be able to flip it off instantly in case of regressions
//     without redeploying the backend.
//
// Two layers of control:
//
//   1. VITE_ENABLED_VIDEO_DRAFT env var: master kill switch. Set to "false"
//      to disable the new flow for everyone. Defaults to enabled.
//
//   2. Style allowlist below: only the styles that have a director_v1 agent
//      already created in agent-config (Phase 1 onwards) are eligible.
//      The rest fall through to the legacy flow.
//
// As more directors come online (sassy_v1, ugc_v1, podcast_v1, modeling_v1
// in Phases 5/6), we add their style_ids to the allowlist.

const STYLES_WITH_DIRECTOR_AGENT: ReadonlySet<string> = new Set([
  "animated-problem",
  "sassy-object", // Phase 5 — video_director_sassy_v1 creado en agent-config dev
  "ugc-testimonial", // Phase 6 — Seedance 2.0 reference-to-video (ecommerce-service #665 + CE #138)
  // "podcast-style",      // Phase 6
  // "product-modeling",   // Phase 6
]);

function readKillSwitch(): boolean {
  const raw = import.meta.env.VITE_ENABLED_VIDEO_DRAFT;
  if (raw === undefined || raw === null || raw === "") return true;
  if (typeof raw === "string") {
    return raw.toLowerCase() !== "false" && raw !== "0";
  }
  return Boolean(raw);
}

/**
 * Returns true when the new draft preview flow should be used for the given
 * style instead of the legacy direct-render flow.
 */
export function useVideoDraftFlag(styleId: string | null | undefined): boolean {
  if (!styleId) return false;
  if (!readKillSwitch()) return false;
  return STYLES_WITH_DIRECTOR_AGENT.has(styleId);
}
